import { itemSyncStatusResponseSchema } from '@tempo-io/work-integration-dto';
import { z } from 'zod';
import { tempoApi } from '@/common/api/tempoApi';
import type { ItemSyncStatusResponse } from '@/common/api/types/workIntegrations';
import { chunkBy } from '@/common/utils/chunkBy';

const ITEM_SYNC_STATUS_BATCH_LIMIT = 50;

export const getItemSyncStatuses = async ({
  itemIds,
}: {
  itemIds: Set<number>;
}): Promise<Record<number, ItemSyncStatusResponse>> => {
  if (itemIds.size === 0) return {};

  const chunks = chunkBy(Array.from(itemIds), ITEM_SYNC_STATUS_BATCH_LIMIT);
  const all: Record<number, ItemSyncStatusResponse> = {};

  for (const ids of chunks) {
    const response = await tempoApi().post('api/v1/work-integration/items/sync-status', {
      json: { itemIds: ids },
    });
    const part = z
      .record(z.coerce.number().int(), itemSyncStatusResponseSchema)
      .parse(await response.json());
    Object.assign(all, part);
  }

  return all;
};

export const requestItemSync = async ({
  itemId,
  integrationId,
}: {
  itemId: number;
  integrationId?: number;
}): Promise<void> => {
  await tempoApi().post(`api/v1/work-integration/items/${itemId}/sync`, {
    searchParams: integrationId !== undefined ? { integrationId } : undefined,
  });
};

const markItemAsPending = async ({
  itemId,
  integrationId,
}: {
  itemId: number;
  integrationId: number;
}): Promise<ItemSyncStatusResponse> => {
  const params = new URLSearchParams({ integrationId: String(integrationId) });
  const response = await tempoApi()
    .post(`api/v1/work-integration/items/${itemId}/sync-status/pending?${params.toString()}`)
    .json();
  return itemSyncStatusResponseSchema.parse(response);
};

const replacePendingIntegration = async ({
  itemId,
  integrationId,
}: {
  itemId: number;
  integrationId: number;
}): Promise<ItemSyncStatusResponse> => {
  const params = new URLSearchParams({ integrationId: String(integrationId) });
  const response = await tempoApi()
    .put(`api/v1/work-integration/items/${itemId}/sync-status/pending?${params.toString()}`)
    .json();
  return itemSyncStatusResponseSchema.parse(response);
};

export const ItemSyncStatusApi = {
  getItemSyncStatuses,
  requestItemSync,
  markItemAsPending,
  replacePendingIntegration,
} as const;
